import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { RemotePageMountOptions, RemotePageMountApi, FederatedAuth, RemotePageMountFn } from '@platform-ui/remote-types';
import { getFloatingRoot } from '@aero/ui';
import { authService } from '@/auth';
import { capabilityBus } from '@/services/capability-bus';
import { getSubpath } from '@/utils/route-utils';
import { DomGuardrail } from '@/utils/dom-guardrail';
import styles from './loading-spinner.module.css';
import { AppConfig } from '@/config/env';
import { MountStatus } from '@/domain/types';

/**
 * Loading spinner shown while the remote app is mounting.
 */
function LoadingSpinner({ appName }: { appName: string }) {
    return (
        <div className={styles.container}>
            <div className={styles.spinner} />
            <span className={styles.text}>Loading {appName}...</span>
        </div>
    );
}

/**
 * Shape of the module exported by the remote page.
 */
interface PageModule {
    getRemotePageMount: RemotePageMountFn;
}

/**
 * Props for the PageHost component.
 * Uses framework-agnostic FederatedAuth from remote-types.
 */
type PageHostProps = {
    config: AppConfig;
    onNavigate?: (path: string, replace: boolean) => void;
    /** Host-provided federated auth interface to pass to remote apps */
    federatedAuth?: FederatedAuth;
};

/**
 * Host component for remote page modules.
 *
 * Loads and mounts a federated page from a remote app, handling:
 * - Async module loading with error handling
 * - Bidirectional navigation sync between host and remote
 * - FederatedAuth injection for remote auth access
 * - Loading state management
 *
 * Navigation Flow:
 * - Host → Remote: When host URL changes, calls api.navigate(subpath)
 * - Remote → Host: Remote calls onNavigate(path), host updates URL
 */
export function PageHost({ config, onNavigate, federatedAuth: propsFederatedAuth }: PageHostProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const apiRef = useRef<RemotePageMountApi | null>(null);
    const guardrailRef = useRef<DomGuardrail | null>(null);
    const location = useLocation();
    const navigate = useNavigate();

    // Track if navigation originated from remote (prevents echo)
    const isRemoteNavigationRef = useRef(false);

    // Store latest navigation handler in ref for stable closure
    const navigationHandlerRef = useRef<((path: string, replace: boolean) => void) | null>(null);

    const [status, setStatus] = useState<MountStatus>('loading');

    // Default navigation handler - prefixes basePath to remote paths
    const defaultNavigationHandler = useCallback(
        (path: string, replace: boolean) => {
            // When basePath is '/', don't prefix to avoid double slashes
            const fullPath = config.basePath === '/' ? path : `${config.basePath}${path}`;
            navigate(fullPath, { replace });
        },
        [config.basePath, navigate]
    );

    // Keep navigation handler ref updated
    useEffect(() => {
        navigationHandlerRef.current = onNavigate ?? defaultNavigationHandler;
    }, [onNavigate, defaultNavigationHandler]);

    // Capture location at mount time for initialPath (ref avoids re-triggering mount)
    const locationRef = useRef(location);
    locationRef.current = location;

    // Mount the remote app
    useEffect(() => {
        let cancelled = false;

        setStatus('loading');

        (async () => {
            try {
                if (!config.importRemotePageMount) {
                    console.warn('[PageHost] No importRemotePageMount configured for', config.appName);
                    setStatus('error');
                    return;
                }

                if (!containerRef.current) return;

                // Start DOM guardrail BEFORE the dynamic import so we catch
                // <link> tags injected by Vite's CSS code-splitting during chunk loading.
                guardrailRef.current?.stop();
                const guardrail = new DomGuardrail(config.appName, containerRef.current);
                guardrail.start();
                guardrailRef.current = guardrail;

                const module = (await config.importRemotePageMount()) as PageModule;
                if (cancelled || !containerRef.current) return;

                const initialSubpath = getSubpath(locationRef.current.pathname, config.basePath);

                const mountOptions: RemotePageMountOptions = {
                    basePath: config.basePath,
                    initialPath: initialSubpath,
                    onNavigate: (path: string, replace: boolean) => {
                        // Mark as remote-initiated to prevent sync echo
                        isRemoteNavigationRef.current = true;
                        navigationHandlerRef.current?.(path, replace);
                        // Reset after microtask (navigation completes)
                        queueMicrotask(() => {
                            isRemoteNavigationRef.current = false;
                        });
                    },
                    federatedAuth: propsFederatedAuth ?? authService.createFederatedAuth(),
                    floatingRoot: getFloatingRoot(),
                    capabilityBus,
                    onReady: () => {
                        if (!cancelled) setStatus('mounted');
                    }
                };

                apiRef.current = module.getRemotePageMount(containerRef.current, mountOptions);

                // Fallback for apps that don't call onReady (sync React apps).
                queueMicrotask(() => {
                    if (cancelled || !apiRef.current) return;
                    setStatus(prev => (prev === 'loading' ? 'mounted' : prev));
                });
            } catch (err) {
                console.error('[PageHost] Failed to mount:', err);
                if (!cancelled) setStatus('error');
            }
        })();

        return () => {
            apiRef.current?.unmount();
            apiRef.current = null;
            guardrailRef.current?.stop();
            guardrailRef.current = null;
            cancelled = true;
        };
        // Remount only when the remote module, basePath, or auth bridge changes.
        // In-app URL changes are forwarded via the URL-sync effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.importRemotePageMount, config.basePath, propsFederatedAuth]);

    // Sync cleanup when the mount deps change (useLayoutEffect runs before paint)
    useLayoutEffect(() => {
        return () => {
            apiRef.current?.unmount();
            apiRef.current = null;
            guardrailRef.current?.stop();
            guardrailRef.current = null;
        };
    }, [config.importRemotePageMount, config.basePath, propsFederatedAuth]);

    // Sync host URL changes to remote
    useEffect(() => {
        // Skip if: no API, remote initiated this change, or path doesn't match basePath
        if (!apiRef.current || isRemoteNavigationRef.current) return;

        const { pathname } = location;
        const { basePath } = config;

        // Only sync if pathname belongs to this app
        if (basePath !== '/' && !pathname.startsWith(basePath)) return;

        const subpath = getSubpath(pathname, basePath);
        if (apiRef.current.getPath() !== subpath) {
            apiRef.current.navigate(subpath);
        }
    }, [location, config]);

    return (
        <div style={{ position: 'relative', minHeight: '100%' }}>
            {status === 'loading' && <LoadingSpinner appName={config.appName} />}
            {status === 'error' && (
                <div className={styles.container}>
                    <span className={styles.text}>Failed to load {config.appName}</span>
                </div>
            )}
            <div
                ref={containerRef}
                data-mf-container-host="page"
            />
        </div>
    );
}
